import dispatchGithubWorkflow from '@/api/github_action/dispatchGithubWorkflow'
import getListWorkflow from '@/api/github_action/getListWorkflow'
import { useConfigStore } from '@/stores/config'
import { computed, reactive, ref } from 'vue'
import type { PayloadEnv } from '~~/types'
import { useSnackbar } from './useSnackbar'

type EnvironmentKey = 'ballbot' | 'venus' | 'production' | 'prod-support'

interface TestCaseEnv {
  testCases: any[]
  selected: any[]
}

export function useApiAutomation() {
  const configStore = useConfigStore()
  const { showSnackbar, handleError } = useSnackbar()

  // State
  const isConfirmDialogActive = ref(false)
  const selectedEnvironment = ref<string | null>(null)
  const isLoadingTestCases = ref(false)
  const testCases = ref<any[]>([])
  const totalRuns = ref({ ballbot: 0, venus: 0, production: 0, 'prod-support': 0 })
  const successfulRuns = ref({ ballbot: 0, venus: 0, production: 0, 'prod-support': 0 })
  const failedRuns = ref({ ballbot: 0, venus: 0, production: 0, 'prod-support': 0 })
  const recentActivities = ref<any[]>([])

  const activateTestCase = reactive<Record<EnvironmentKey, TestCaseEnv>>({
    ballbot: { testCases: [], selected: [] },
    venus: { testCases: [], selected: [] },
    production: { testCases: [], selected: [] },
    'prod-support': { testCases: [], selected: [] },
  })

  // Computed
  const dynamicEnvironments = computed(() => {
    const envs: Record<string, string> = {}
    configStore.environments.forEach((env: any) => {
      if (env.name === 'hh-ballbot') envs.ENV_HH_BALLBOT = env.id
      else if (env.name === 'hh-venus') envs.ENV_HH_VENUS = env.id
      else if (env.name === 'production') envs.ENV_PRODUCTION = env.id
      else if (env.name === 'prod-support') envs.ENV_PROD_SUPPORT = env.id
    })
    return envs
  })

  const sortedRecentActivities = computed(() => {
    return recentActivities.value
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, 12)
  })

  const latestCompletedActivity = computed(() => {
    return (
      sortedRecentActivities.value
        .filter(activity => activity.status === 'completed')
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0] || null
    )
  })

  // Helper functions
  const mapEnvironment = (envId: string): EnvironmentKey => {
    for (const env of configStore.environments) {
      if (env.id === envId) {
        if (env.name === 'hh-ballbot') return 'ballbot'
        else if (env.name === 'hh-venus') return 'venus'
        else if (env.name === 'production') return 'production'
        else if (env.name === 'prod-support') return 'prod-support'
      }
    }
    return 'ballbot'
  }

  const getLatestActivityForEnv = (env: EnvironmentKey) => {
    return (
      recentActivities.value
        .filter(activity => activity.environment === env)
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0] || null
    )
  }

  const isDisabledButton = (env: EnvironmentKey): boolean => {
    const noTestCases = activateTestCase[env].testCases.length === 0 || activateTestCase[env].selected.length === 0
    const latestActivity = getLatestActivityForEnv(env)
    const isRunInProgress =
      latestActivity && ['in_progress', 'queued', 'pending', 'waiting'].includes(latestActivity.status)
    return noTestCases || isRunInProgress
  }

  // Dialog handlers
  const openConfirmDialog = (environment: string) => {
    selectedEnvironment.value = environment
    isConfirmDialogActive.value = true
  }

  const closeConfirmDialog = () => {
    selectedEnvironment.value = null
    isConfirmDialogActive.value = false
  }

  // API functions
  const fetchWorkflowRunsForEnvironment = async (environment: EnvironmentKey) => {
    try {
      const queries = [
        { key: 'totalAutomations', query: { per_page: 12 } },
        { key: 'successfulAutomations', query: { status: 'completed', per_page: 1 } },
        { key: 'failedAutomations', query: { status: 'failed', per_page: 1 } },
      ]

      for (const { key, query } of queries) {
        const response = await getListWorkflow(environment as PayloadEnv, query)
        if (response.success) {
          const totalCount = response.data?.totalCount || 0
          if (key === 'totalAutomations') {
            totalRuns.value[environment] = totalCount
            const workflowRunsWithEnv = (response.data?.workflowRuns || []).map((run: any) => ({
              ...run,
              environment,
            }))
            recentActivities.value.push(...workflowRunsWithEnv)
          } else if (key === 'successfulAutomations') {
            successfulRuns.value[environment] = totalCount
          } else if (key === 'failedAutomations') {
            failedRuns.value[environment] = totalCount
          }
        } else {
          // Silently handle "Not Found" errors (e.g., workflow file doesn't exist)
          if (response.message && response.message.toLowerCase().includes('not found')) {
            console.warn(`Workflow not found for ${environment}: ${response.message}`)
          } else {
            throw new Error(response.message)
          }
        }
      }
    } catch (error: any) {
      handleError(error)
    }
  }

  const fetchWorkflowRuns = async () => {
    await Promise.all([
      fetchWorkflowRunsForEnvironment('ballbot'),
      fetchWorkflowRunsForEnvironment('venus'),
      fetchWorkflowRunsForEnvironment('production'),
      fetchWorkflowRunsForEnvironment('prod-support'),
    ])
  }

  const fetchAllTestCases = async () => {
    try {
      isLoadingTestCases.value = true
      const response = await $fetch('/api/setting/test-cases-environment', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })

      if (response.success && 'data' in response) {
        const data = response.data
        const grouped: Record<string, any[]> = {}

        ;(data as any[]).forEach(item => {
          if (item.enabled) {
            const envId = item.environmentId
            if (!grouped[envId]) {
              grouped[envId] = []
            }
            grouped[envId].push(item)
          }
        })

        await configStore.fetchDataEnvironment()

        const envMapping: Record<string, string> = {}
        configStore.environments.forEach((env: any) => {
          envMapping[env.id] = env.name
        })

        // Clear previous test cases
        activateTestCase.ballbot.testCases = []
        activateTestCase.venus.testCases = []
        activateTestCase.production.testCases = []
        activateTestCase['prod-support'].testCases = []

        // Map test cases dynamically based on environment names
        Object.keys(grouped).forEach(envId => {
          const envName = envMapping[envId]
          const sortedTestCases = (grouped[envId] || []).sort((a, b) =>
            a.testCases.name.localeCompare(b.testCases.name),
          )
          const sortedNames = sortedTestCases.map(item => item.testCases.name)

          if (envName === 'hh-ballbot') {
            activateTestCase.ballbot.testCases = sortedTestCases
            activateTestCase.ballbot.selected = sortedNames
          } else if (envName === 'hh-venus') {
            activateTestCase.venus.testCases = sortedTestCases
            activateTestCase.venus.selected = sortedNames
          } else if (envName === 'production') {
            activateTestCase.production.testCases = sortedTestCases
            activateTestCase.production.selected = sortedNames
          } else if (envName === 'prod-support') {
            activateTestCase['prod-support'].testCases = sortedTestCases
            activateTestCase['prod-support'].selected = sortedNames
          }
        })
      } else {
        console.warn('Invalid response:', response)
        testCases.value = []
      }
    } catch (error) {
      console.error('Error fetching test cases:', error)
    } finally {
      isLoadingTestCases.value = false
    }
  }

  const executeAutomation = async () => {
    try {
      if (!selectedEnvironment.value) {
        throw new Error('No environment selected.')
      }
      const env = mapEnvironment(selectedEnvironment.value)

      const allSelected =
        activateTestCase[env]?.selected.length === activateTestCase[env]?.testCases.length &&
        activateTestCase[env]?.testCases.length > 0

      const scenarios = allSelected ? 'all' : activateTestCase[env]?.selected.map(e => `${e}.spec.ts`).join(' ')

      if (!scenarios || scenarios.length === 0) {
        throw new Error('No test cases selected.')
      }

      console.log(`Triggering automation for ${env} with scenarios:`, scenarios)
      const { success, message } = await dispatchGithubWorkflow(env, scenarios)
      if (success) {
        showSnackbar(message, 'success')
        setTimeout(() => window.location.reload(), 1500)
      } else {
        throw new Error(message)
      }
    } catch (error: any) {
      handleError(error)
    } finally {
      closeConfirmDialog()
    }
  }

  return {
    // State
    isConfirmDialogActive,
    selectedEnvironment,
    isLoadingTestCases,
    testCases,
    totalRuns,
    successfulRuns,
    failedRuns,
    recentActivities,
    activateTestCase,
    // Computed
    dynamicEnvironments,
    sortedRecentActivities,
    latestCompletedActivity,
    // Functions
    mapEnvironment,
    isDisabledButton,
    openConfirmDialog,
    closeConfirmDialog,
    fetchWorkflowRuns,
    fetchAllTestCases,
    executeAutomation,
  }
}
